import React from 'react';
import { LatticeSettings } from './LatticeSettings';
import { Loader, TreeDeciduous, GitBranch } from 'lucide-react';

/**
 * Mode Selection Panel - Support style selection
 * Simplified: removed Fast/Pro mode, focused on tree styles
 */
export const ModeSelection = ({
  mode,
  setMode,
  supportType,
  setSupportType,
  treeStyle,
  setTreeStyle,
  showAdvancedSettings,
  setShowAdvancedSettings,
  latticeSpacing,
  setLatticeSpacing,
  latticeWidth,
  setLatticeWidth,
  savingPreferences
}) => {
  return (
    <div className="bg-bg-light p-4 rounded-lg border border-text-muted/20 mb-6" data-testid="mode-selection">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold">Support Style</h3>
        {savingPreferences && (
          <span className="text-xs text-accent flex items-center gap-1">
            <Loader className="w-3 h-3 animate-spin" />
            Saving...
          </span>
        )}
      </div>
      
      {/* Tree Style Cards */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Forest Style */}
        <button
          onClick={() => setTreeStyle('forest')}
          className={`p-4 rounded-lg border-2 transition-all text-left ${
            treeStyle === 'forest'
              ? 'border-accent bg-accent/10'
              : 'border-text-muted/20 hover:border-text-muted/40'
          }`}
          data-testid="tree-style-forest"
        >
          <div className="flex items-center gap-2 mb-2">
            <TreeDeciduous className={`w-5 h-5 ${treeStyle === 'forest' ? 'text-accent' : 'text-text-muted'}`} />
            <span className={`font-medium ${treeStyle === 'forest' ? 'text-white' : 'text-text-secondary'}`}>
              Forest
            </span>
            {treeStyle === 'forest' && (
              <span className="text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full ml-auto">Active</span>
            )}
          </div>
          <p className="text-xs text-text-secondary">
            Multiple independent trees with lattice roofs for flat surfaces
          </p>
          <div className="mt-2 text-xs text-green-400">✓ Recommended for most models</div>
        </button>

        {/* Hierarchical Style — Coming Soon */}
        <div
          className="p-4 rounded-lg border-2 border-text-muted/10 bg-bg-dark/30 text-left opacity-60 cursor-not-allowed relative"
          data-testid="tree-style-hierarchical"
        >
          <div className="flex items-center gap-2 mb-2">
            <GitBranch className="w-5 h-5 text-text-muted" />
            <span className="font-medium text-text-muted">
              Hierarchical
            </span>
            <span className="text-[10px] bg-primary/20 text-primary px-2 py-0.5 rounded-full ml-auto font-semibold tracking-wide uppercase">Coming Soon</span>
          </div>
          <p className="text-xs text-text-muted">
            Single main trunk with branching sub-trunks (OrcaSlicer-inspired)
          </p>
        </div>
      </div>

      {/* Style Description */}
      <div className="p-3 bg-bg-dark/50 rounded-lg mb-4">
        <div className="text-sm text-text-secondary">
          <span className="text-white font-medium">Forest Mode:</span> Creates multiple independent tree supports, 
          each growing from the build plate. Includes lattice roofs for flat horizontal surfaces. 
          Best for complex models with many overhangs.
        </div>
      </div>

      {/* Auto-saved indicator */}
      <p className="text-xs text-text-secondary mb-4">
        <span className="text-accent">•</span> Settings auto-saved to your account
      </p>
      
      {/* Lattice Settings */}
      <LatticeSettings
        showAdvancedSettings={showAdvancedSettings}
        setShowAdvancedSettings={setShowAdvancedSettings}
        latticeSpacing={latticeSpacing}
        setLatticeSpacing={setLatticeSpacing}
        latticeWidth={latticeWidth}
        setLatticeWidth={setLatticeWidth}
      />
    </div>
  );
};

export default ModeSelection;
